/**
 * Custom-attribute filter of the My Portfolio Analytics "Resource & capacity" tab (SPEC-0052 v1, C4).
 *
 * A filter is { target: "PROJECT" | "SCENARIO", attributeId, values: string[] }. Values within one
 * filter are OR-ed, filters are AND-ed. Usage rows carry a scenario id, so the filters resolve to
 * the set of scenario ids that stay visible; a project attribute resolves through the scenario's
 * project.
 */

export const TARGET_PROJECT = "PROJECT";
export const TARGET_SCENARIO = "SCENARIO";
// Any field of the chart field catalogue (SPEC-0053): attributeId holds the field key
// ("project.status", "resource.type"…) and values its value ids. Applied row by row.
export const TARGET_FIELD = "FIELD";

/** The comparable tokens of one stored attribute value (free text, or the selected options). */
export const valueTokens = (value) => {
  if (!value) return [];
  if (value.text_value) return [String(value.text_value)];
  return (value.selections || [])
    .map((s) => s && s.option)
    .filter((o) => o != null && o !== "")
    .map(String);
};

const valueOf = (valuesMap, entityId, attributeId) => {
  if (entityId == null || !valuesMap) return null;
  const list = valuesMap[entityId] || valuesMap[String(entityId)] || [];
  return list.find((v) => v && v.attribute_id === attributeId) || null;
};

/** Distinct tokens found for one attribute across the entities of the portfolio, sorted. */
export const distinctValueTokens = (valuesMap, attributeId) => {
  const seen = new Set();
  Object.keys(valuesMap || {}).forEach((entityId) => {
    valueTokens(valueOf(valuesMap, entityId, attributeId)).forEach((t) => seen.add(t));
  });
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
};

/** Drops filters that can no longer apply: no values selected, or an attribute that was removed. */
export const activeFilters = (filters, projectAttributes, scenarioAttributes) =>
  (filters || []).filter((f) => {
    if (!f || !Array.isArray(f.values) || f.values.length === 0) return false;
    if (f.target === TARGET_FIELD) return typeof f.attributeId === "string" && f.attributeId.length > 0;
    const known = f.target === TARGET_PROJECT ? projectAttributes : scenarioAttributes;
    return (known || []).some((a) => a.id === f.attributeId);
  });

/**
 * @returns {Set<string>|null} ids (as strings) of the scenarios that pass every filter,
 *   or null when nothing is filtered.
 */
export const buildAllowedScenarioIds = (
  filters,
  { projectValuesMap, scenarioValuesMap, scenarioProjectMap, scenarioIds }
) => {
  filters = (filters || []).filter((f) => f.target !== TARGET_FIELD);
  if (filters.length === 0) return null;
  const candidates = scenarioIds || Object.keys(scenarioProjectMap || {});
  const allowed = new Set();
  candidates.forEach((scenarioId) => {
    const ok = filters.every((f) => {
      const wanted = new Set(f.values.map(String));
      const value =
        f.target === TARGET_PROJECT
          ? valueOf(
              projectValuesMap,
              (scenarioProjectMap || {})[scenarioId],
              f.attributeId
            )
          : valueOf(scenarioValuesMap, scenarioId, f.attributeId);
      return valueTokens(value).some((t) => wanted.has(t));
    });
    if (ok) allowed.add(String(scenarioId));
  });
  return allowed;
};

/** Keeps rows that are not scenario-scoped (capacity) and rows of an allowed scenario. */
export const filterUsagesByScenarios = (usages, allowed) => {
  if (!allowed || !usages) return usages;
  return usages.filter((u) => u.scenarioId == null || allowed.has(String(u.scenarioId)));
};

export const filterKey = (target, attributeId) => `${target}-${attributeId}`;

/** The field filters of the page, in the `{ name, items }` shape of the chart filters. */
export const fieldFilters = (filters) =>
  (filters || [])
    .filter((f) => f && f.target === TARGET_FIELD && Array.isArray(f.values) && f.values.length > 0)
    .map((f) => ({ name: f.attributeId, items: f.values }));

/**
 * The attributes a user can filter by, each with its caption: project attributes first, then
 * scenario attributes, in the order the tenant defined them.
 * @param {{ id: number, attribute_name: string }[]} projectAttributes
 * @param {{ id: number, attribute_name: string }[]} scenarioAttributes
 */
export const filterableAttributes = (projectAttributes, scenarioAttributes) => [
  ...(projectAttributes || []).map((a) => ({ target: TARGET_PROJECT, attributeId: a.id, name: a.attribute_name })),
  ...(scenarioAttributes || []).map((a) => ({ target: TARGET_SCENARIO, attributeId: a.id, name: a.attribute_name })),
];

/** Case-insensitive "contains" on the attribute name; an empty query keeps everything. */
export const searchAttributes = (attributes, query) => {
  const q = (query || "").trim().toLowerCase();
  return q ? attributes.filter((a) => (a.name || "").toLowerCase().includes(q)) : attributes;
};

/**
 * Case-insensitive "contains" on the values, capped: a free-text attribute can have hundreds of
 * distinct values.
 * @returns {{ shown: string[], total: number }}
 */
export const searchValues = (values, query, limit = 200) => {
  const q = (query || "").trim().toLowerCase();
  const matching = q ? values.filter((v) => v.toLowerCase().includes(q)) : values;
  return { shown: matching.slice(0, limit), total: matching.length };
};

/** "Gold, Silver" or "Gold, Silver +2" for the chip of a filter. */
export const summariseValues = (values, max = 2) => {
  const list = values || [];
  if (list.length <= max) return list.join(", ");
  return `${list.slice(0, max).join(", ")} +${list.length - max}`;
};

/** Sets the values of one filter; no values removes it. Other filters are kept in order. */
export const upsertFilter = (filters, target, attributeId, values) => {
  const rest = (filters || []).filter((f) => !(f.target === target && f.attributeId === attributeId));
  return values.length ? [...rest, { target, attributeId, values }] : rest;
};
